import React, { useEffect, useState, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { useDispatch } from "react-redux";

import axios from "axios";

import { updateOrder } from "./slice/posSlice";
import { setCommonState, setPrintrtTyp, setCompanyInfo } from "commonSlice/commonSlice";
import filterStatusValue from "utils/filterStatusValue";
import CommonBackdrop from "loader/CommonBackdrop";

import { baseUrl, redirectUrl } from "config";

import { useSnackBar } from "context/snackbarContext";
import { Box, Typography, createTheme, ThemeProvider, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useMemo } from "react";

const PRINTER_AND_OTHER_URL = "/getPrinterNameAndOther";
const STATUS_TABLE_DATA_URL = "/main/getStatusDataByTyp";
const COMPANY_DETAILS_URL = "/main/getCompanyDetails";

const OrderLayout = () => {
  const { showSnackbar } = useSnackBar();
  const dispatch = useDispatch();
  const baseTheme = useTheme();
  const posHeader = useSelector((state) => state.posSlice);

  // brown-theme for B2C
  const theme = useMemo(() => {
    if (posHeader.billType === "B2C") {
      return createTheme({
        ...baseTheme,
        palette: {
          ...baseTheme.palette,
          primary: {
            main: "#6D4C41", // Brown
            light: "#EFEBE9", // Very Light Brown
            dark: "#4E342E", // Dark Brown
            contrastText: "#fff",
          },
          secondary: {
            main: "#8D6E63", // Medium-Light Brown
            light: "#BCAAA4",
            dark: "#5D4037",
            contrastText: "#fff",
          },
          info: {
            main: "#795548",
            light: "#A1887F",
            dark: "#4E342E",
          },
        },
      });
    }
    return baseTheme;
  }, [posHeader.billType, baseTheme]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleApiError = useCallback((err) => {
    if (err.response) {
      if (err.response.status === 403 || err.response.status === 401) {
        redirectUrl();
      } else {
        showSnackbar(
          "error",
          `API Error: ${err.response.status} - ${err.response.data?.message || "Something went wrong."
          }`
        );
      }
    } else if (err.request) {
      showSnackbar("error", "Network Error: No response received from server.");
    } else {
      showSnackbar("error", "An unexpected error occurred.");
    }
    setError(err);
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [printerRes, isRestaurantRes, companyRes] = await Promise.all([
          axios.get(baseUrl + PRINTER_AND_OTHER_URL, { withCredentials: true }),
          axios.post(
            baseUrl + STATUS_TABLE_DATA_URL,
            { param: "IS_RESTAURANT" },
            { withCredentials: true }
          ),
          axios.get(baseUrl + COMPANY_DETAILS_URL, { withCredentials: true }),
        ]);

        // Set company info for QR code generation
        if (companyRes.data?.[0]) {
          dispatch(setCompanyInfo(companyRes.data[0]));
        }

        const commonParams = printerRes.data[1];
        const taxUtilities = printerRes.data[2];

        const decimal = filterStatusValue(
          commonParams,
          "params",
          "DECIMAL_VALUE"
        )[0]?.value;

        dispatch(setPrintrtTyp(printerRes.data));
        dispatch(
          setCommonState({
            isADDon:
              filterStatusValue(commonParams, "params", "ADD_ON")[0]?.value ===
              "YES",
            decimalCount: Number(decimal || 3),
            allKot:
              filterStatusValue(commonParams, "params", "ALL_KOT")[0]?.value ===
              "YES",
            isInvoice:
              filterStatusValue(commonParams, "params", "IS_INVOICE")[0]
                ?.value === "YES",
            taxUtilitiesListData: taxUtilities,
            KotEnabled:
              filterStatusValue(commonParams, "params", "KOT_ENABLED")[0]
                ?.value === "YES",
            IsRestaurant:
              filterStatusValue(commonParams, "params", "IS_RESTAURANT")[0]
                ?.value === "YES",
            salesBillDeclaration: filterStatusValue(
              commonParams,
              "params",
              "SALESBILL_DECLARATION"
            )[0]?.value,
            orderFormDeclaration: filterStatusValue(
              commonParams,
              "params",
              "ORDERFORM_DECLARATION"
            )[0]?.value,
            billWiseTax: filterStatusValue(
              commonParams,
              "params",
              "bill_Wise_Tax%"
            )[0]?.value,
            isPaidMethod: filterStatusValue(
              commonParams,
              "params",
              "PAID_METHOD_WINDOW"
            )[0]?.value,
            isDiscount: filterStatusValue(
              commonParams,
              "params",
              "IS_DISCOUNT"
            )[0]?.value,
          })
        );

        if (
          isRestaurantRes.data.success &&
          isRestaurantRes?.data?.data?.[0]?.value
        ) {
          dispatch(
            updateOrder({
              isRestaurant: isRestaurantRes.data.data[0].value === "YES",
              tax: filterStatusValue(
                commonParams,
                "params",
                "bill_Wise_Tax%"
              )[0]?.value,
            })
          );
        } else {
          showSnackbar(
            "warning",
            "Could not get IS_RESTAURANT status table data correctly."
          );
        }
      } catch (err) {
        handleApiError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  if (error && !isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography color="error" variant="h6">
          Failed to load initial data. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CommonBackdrop open={false} />
      <Outlet />
    </ThemeProvider>
  );
};

export default OrderLayout;
